<template>
  <section
    id="user"
    class="account-home user-account-layout"
    ref="accountHomeContainer"
    :class="{ desktop: $isDesktop }"
    data-test="account-home"
  >
    <template v-if="isLoggedIn">
      <header>
        <button class="user-account-back" @click="toggleAccountMenu()">
          <svg
            v-once
            width="100%"
            height="100%"
            viewBox="0 0 26 24"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xml:space="preserve"
            :title="$t('Back')"
            style="
              fill-rule: evenodd;
              clip-rule: evenodd;
              stroke-linejoin: round;
              stroke-miterlimit: 1.41421;
            "
          >
            <path
              d="M12.679,3.045l-8.646,8.914l8.92,8.639c0.669,0.648 0.686,1.714 0.037,2.383c-0.648,0.668 -1.715,0.685 -2.385,0.037l-10.095,-9.777c-0.343,-0.333 -0.511,-0.776 -0.509,-1.219c-0.016,-0.443 0.139,-0.891 0.471,-1.234l9.785,-10.088c0.648,-0.668 1.716,-0.685 2.385,-0.038c0.668,0.648 0.685,1.715 0.037,2.383Z"
            />
            <path
              d="M24.677,2.858l-8.646,8.913l8.921,8.64c0.668,0.647 0.685,1.714 0.037,2.382c-0.648,0.668 -1.716,0.685 -2.385,0.037l-10.095,-9.777c-0.343,-0.333 -0.512,-0.776 -0.509,-1.219c-0.017,-0.442 0.138,-0.891 0.471,-1.234l9.784,-10.088c0.649,-0.668 1.716,-0.685 2.385,-0.037c0.669,0.648 0.686,1.715 0.037,2.383Z"
            />
          </svg>
        </button>

        <nuxt-link :to="$puLocalePath({ name: 'account' })">
          <user-account-users-info />
        </nuxt-link>
      </header>

      <account-dashboard />
      <user-account-footer :href="contactUsLink" />
    </template>
    <template v-else-if="isLoggedOut || authHasErrors">
      <login-form />
      <new-user />
    </template>
    <div v-else class="wrap-spinner">
      <loading-spinner />
    </div>
    <nuxt-to-meta :nuxt2meta="head" />
  </section>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, nextTick } from 'vue'

definePageMeta({
  layout: 'user-account',
})

const { $i18n, $isDesktop, $puLocalePath, $t } = useNuxtApp()
const authStore = useAuthStore()
const appMenuStore = useAppMenuStore()
const accountHomeContainer = ref<Element>()

const scrollDataAttribute = 'data-noscroll'

// From AuthMixin
const isLoggedIn = computed(() => authStore.isLoggedIn)
const isLoggedOut = computed(() => authStore.isLoggedOut)
const authHasErrors = computed(() => authStore.hasErrors)

// From AppMenuMixin
const accountMenuVisible = computed(() => appMenuStore.accountMenuVisible)
const toggleAccountMenu = () => appMenuStore.toggleAccountMenu()

const head = computed(() => ({
  bodyAttrs: {
    class: 'menu-open account-open',
    'data-logged-in': isLoggedIn.value ? 'true' : 'false',
  },
}))

// workaround for webkit bug https://bugs.webkit.org/show_bug.cgi?id=185614
watch(accountMenuVisible, (newValue) => {
  newValue
    ? document.body.setAttribute(scrollDataAttribute, 'true')
    : document.body.removeAttribute(scrollDataAttribute)
})

onBeforeUnmount(() => {
  if (document && document.body && scrollDataAttribute) {
    document.body.removeAttribute(scrollDataAttribute)
  }
})

watch(isLoggedIn, async (newValue) => {
  // need to wait for new elements to be ready before scrolling
  await nextTick()
  if (!newValue) {
    // puscrollto and window scroll to top not working when rendered in side bar
    accountHomeContainer.value?.scrollTo(0, 0)
  }
})

const contactUsLink = computed(
  () =>
    `https://www.pitchup.com/${
      $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
    }about/contact/`,
)
</script>

<style>
@import url('~/assets/css/user-account.css');
</style>
<style scoped>
.wrap-spinner {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
}

#user {
  bottom: 0;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  left: 15vw;
  min-height: 100dvh;
  overflow: auto;
  position: fixed;
  right: 0;
  text-align: left;
  top: 0;
  width: 85vw;
  z-index: 0;
}

header {
  background: var(--colour-mid-green);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

header a {
  text-decoration: none;
  flex-shrink: 0;
}

.user-account-back {
  border: none;
  background: transparent;
  box-shadow: none;
  border-radius: 0;
  transform: none;
}

.user-account-back svg {
  height: 1.35em;
  fill: white;
  width: auto;
}

/* desktop & tablet */
#user.desktop {
  width: 100%;
  left: auto;
  right: auto;
  min-height: auto;
  top: auto;
  position: relative;
  margin: auto;
  max-width: var(--page-width-max);
  padding: 0.25em 0 0;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 5.75em);
}

@media (min-width: 1606px) {
  #user.desktop {
    padding: 0.25em var(--page-width-padding) 0;
  }
}

.account-home.desktop header {
  padding: 0 var(--page-width-padding);
  justify-content: flex-end;
}

.account-home.desktop :deep(.user-name) {
  font-size: 1.25em;
}

.account-home.desktop :deep(.users-info) {
  flex-direction: row-reverse;
  gap: 0.5em;
}

.account-home.desktop :deep(.user-account-back) {
  display: none;
}

@media (min-width: 1606px) {
  .account-home.desktop header {
    border-radius: var(--border-radius);
    padding-right: 0;
  }
}
</style>
<style>
/* opening account dashboard */
[data-logged-in='true'] .account-open #page-wrapper {
  transform: translateX(-100vw);
  /* transition-duration: 0.4s; */
  box-shadow: none;
}

[data-logged-in='true'] .account-open #page-wrapper {
  overflow: visible;
}

[data-logged-in='true'] #user {
  width: 100vw;
  left: 0;
}
</style>
